import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import http from '@lib/http-client'

import {
  ChatContentType,
  ChatDocument,
  ChatListFilters,
  ChatListItem,
  ChatListMessage,
  ChatListResponse,
  ChatListResultItem,
  ChatMessage,
  ChatState,
  FLIPPER_WS_CONTENT_TYPE,
  FLIPPER_WS_EVENT_TYPE,
  FlipperWsEventBase,
  FlipperWsEventType,
  IncomingMessageNotification,
  MemberListState,
  PendingChatMessage,
  ServerMessage
} from './types'
import { useAuthStore } from '../Auth'
import uuidHex from '@utils/uuid-hex'
import websocketClient from '@lib/websocketClient'
import { converMessagePayloadToFlipper } from '@mappers/message'
import { toUtcIsoString } from '@utils/date'

const RECONNECT_INTERVAL = 3000
const HEARTBEAT_INTERVAL = 5000
// a message Flipper fails to save (it stops reading the socket) stops coming back
const MAX_SEND_ATTEMPTS = 3
// how long a sent message waits for its confirmation before the history is read
const DELIVERY_CHECK_DELAY = 10000
export const DEFAULT_CHAT_PAGE_SIZE = 10

let socket: WebSocket | null = null
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
let heartbeatInterval: ReturnType<typeof setInterval> | null = null
let deliveryCheckTimeout: ReturnType<typeof setTimeout> | null = null
let intentionalDisconnect = false

const isSocketOpen = () => !!socket && socket.readyState === WebSocket.OPEN

const assertAccessToken = () => {
  if (!useAuthStore.getState().accessToken) {
    throw new Error('Missing access token for Flipper request')
  }
}

const toDocumentId = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }
  return undefined
}
/**
 * Backend history: FILE messages include metadata in `document` (Flipper) or legacy
 * `documents` / `attachment`; `value` is the document id.
 */

const normalizeWsEventType = (raw: unknown): FlipperWsEventType | null => {
  if (typeof raw === 'number') {
    switch (raw) {
      case FLIPPER_WS_EVENT_TYPE.SYSTEM:
        return 'SYSTEM'
      case FLIPPER_WS_EVENT_TYPE.MESSAGE:
        return 'MESSAGE'
      case FLIPPER_WS_EVENT_TYPE.TYPING:
        return 'TYPING'
      case FLIPPER_WS_EVENT_TYPE.MSG_RECEIVED:
        return 'MSG_RECEIVED'
      default:
        return null
    }
  }

  const value = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (value === 'SYSTEM' || value === '0') return 'SYSTEM'
  if (value === 'MESSAGE' || value === '1') return 'MESSAGE'
  if (value === 'TYPING' || value === '2') return 'TYPING'
  if (value === 'MSG_RECEIVED' || value === '3') return 'MSG_RECEIVED'
  return null
}

const normalizeContentType = (raw?: unknown): ChatContentType => {
  if (typeof raw === 'number') {
    if (raw === FLIPPER_WS_CONTENT_TYPE.FILE) return 'FILE'
    if (raw === FLIPPER_WS_CONTENT_TYPE.BUTTON) return 'BUTTON'
    return 'TEXT'
  }

  const upper = String(raw ?? 'TEXT')
    .trim()
    .toUpperCase()
  if (upper === 'FILE' || upper === '3') return 'FILE'
  if (upper === 'BUTTON' || upper === '2') return 'BUTTON'
  return 'TEXT'
}

const parseFlipperWsEvent = (raw: unknown): FlipperWsEventBase | null => {
  if (typeof raw !== 'string') {
    return null
  }

  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    return parsed as FlipperWsEventBase
  } catch {
    return null
  }
}

const isRoomScopedEvent = (type: FlipperWsEventType | null): boolean =>
  type === 'MESSAGE' || type === 'TYPING'

const belongsToActiveRoom = (
  event: FlipperWsEventBase,
  activeRoomId: number | null
): boolean =>
  activeRoomId !== null &&
  typeof event.room === 'number' &&
  event.room === activeRoomId

const normalizeDocuments = (
  rawDocuments: Record<string, any> | undefined,
  value: unknown,
  contentType: ChatContentType
): ChatDocument | undefined => {
  if (contentType !== 'FILE') {
    return undefined
  }

  const documentId = toDocumentId(value)
  if (documentId === undefined) {
    return undefined
  }

  const source = rawDocuments

  return {
    file_name: source?.file_name ?? 'File',
    file_size:
      typeof source?.file_size === 'number'
        ? source.file_size
        : typeof source?.size_bytes === 'number'
        ? source.size_bytes
        : 0,
    mime_type: source?.mime_type ?? ''
  }
}

const getRawDocumentMeta = (raw: Record<string, any>) =>
  raw.documents ?? raw.document ?? raw.attachment

const normalizeServerMessage = (raw: Record<string, any>): ServerMessage => {
  const contentType = normalizeContentType(raw.content_type)
  const eventType = normalizeWsEventType(raw.type)
  const ownerId = raw.owner_id
  const userId = raw.user_id
  return {
    id: String(raw.id ?? raw.message_id ?? ''),
    type: eventType === 'MSG_RECEIVED' || !eventType ? 'MESSAGE' : eventType,
    value: raw.value ?? raw.text ?? '',
    content_type: contentType,
    documents: normalizeDocuments(
      getRawDocumentMeta(raw),
      raw.value,
      contentType
    ),
    translation: raw.translation,
    owner_id: ownerId,
    user_id: userId,
    walrus_ai: Boolean(raw.walrus_ai ?? (ownerId === 0 || userId === 0)),
    timestamp:
      toUtcIsoString(raw.timestamp ?? raw.created ?? raw._created) ??
      raw.timestamp ??
      raw.created ??
      raw._created
  }
}

const isDocumentIdShadowText = (
  msg: ServerMessage,
  allMessages: ServerMessage[]
): boolean => {
  if (msg.content_type !== 'TEXT') {
    return false
  }

  const docId = toDocumentId(msg.value)
  if (docId === undefined || String(msg.value).trim() !== String(docId)) {
    return false
  }

  return allMessages.some(
    other =>
      other.content_type === 'FILE' && toDocumentId(other.value) === docId
  )
}

const buildFileSenderByDocumentId = (
  messages: ServerMessage[],
  professionalUserId?: number
): Map<number, boolean> => {
  const map = new Map<number, boolean>()
  if (professionalUserId === undefined) {
    return map
  }

  messages.forEach(msg => {
    if (msg.content_type !== 'TEXT') {
      return
    }

    const docId = toDocumentId(msg.value)
    if (docId === undefined || String(msg.value).trim() !== String(docId)) {
      return
    }

    map.set(docId, msg.owner_id === professionalUserId)
  })

  return map
}

const serverToChatMessage = (
  msg: ServerMessage,
  memberUserId: number,
  options: { isSender?: boolean; professionalUserId?: number } = {}
): ChatMessage => {
  const contentType = msg.content_type || 'TEXT'
  const content =
    contentType === 'FILE'
      ? msg.documents?.file_name || 'File'
      : String(msg.value)

  const isSender = options.isSender ?? msg.owner_id !== memberUserId
  const ownerUserId = msg.owner_id ?? msg.user_id

  const documentId =
    contentType === 'FILE' ? toDocumentId(msg.value) : undefined

  return {
    id: msg.id,
    content,
    isSender,
    contentType,
    documents: msg.documents,
    documentId,
    translation: msg.translation,
    ownerUserId,
    walrus_ai: msg.walrus_ai,
    timestamp: msg.timestamp
  }
}

const processServerMessages = (
  messages: ServerMessage[],
  memberUserId: number,
  professionalUserId?: number
): ChatMessage[] => {
  const fileSenderByDocId = buildFileSenderByDocumentId(
    messages,
    professionalUserId
  )
  const filtered = messages.filter(
    msg => !isDocumentIdShadowText(msg, messages)
  )

  return filtered.map(msg => {
    const docId =
      msg.content_type === 'FILE' ? toDocumentId(msg.value) : undefined
    const shadowSender =
      docId !== undefined ? fileSenderByDocId.get(docId) : undefined

    return serverToChatMessage(msg, memberUserId, {
      professionalUserId,
      ...(shadowSender !== undefined ? { isSender: shadowSender } : {})
    })
  })
}

const mergeMessages = (
  existing: ChatMessage[],
  incoming: ChatMessage[]
): ChatMessage[] => {
  const byId = new Map<string, ChatMessage>()

  existing.forEach(message => {
    byId.set(message.id, message)
  })

  incoming.forEach(message => {
    const current = byId.get(message.id)
    byId.set(message.id, {
      ...message,
      isPending: current?.isPending ?? message.isPending
    })
  })

  return Array.from(byId.values()).sort((a, b) => {
    const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0
    const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0
    return aTime - bTime
  })
}

const appendMessageForUser = (
  messagesByUserId: Record<number, ChatMessage[]>,
  userId: number,
  message: ChatMessage
) => {
  const existing = messagesByUserId[userId] || []
  if (existing.some(item => item.id === message.id)) {
    return {
      ...messagesByUserId,
      [userId]: existing.map(item =>
        item.id === message.id
          ? { ...item, ...message, isPending: false }
          : item
      )
    }
  }

  return {
    ...messagesByUserId,
    [userId]: [...existing, message]
  }
}

// Takes a message out of the queue and gives its bubble the outcome. After a
// reload the bubble is drawn from the queue alone, so leaving the queue would
// take it off the screen: it gets a bubble of its own.
const settlePendingMessage = (
  state: ChatState,
  messageId: string,
  outcome: Pick<ChatMessage, 'isPending' | 'sendFailed' | 'errorMessage'>
): Pick<ChatState, 'pendingMessagesById' | 'messagesByUserId'> => {
  const pending = state.pendingMessagesById[messageId]
  const messagesByUserId: Record<number, ChatMessage[]> = Object.fromEntries(
    Object.entries(state.messagesByUserId).map(([userId, messages]) => [
      userId,
      messages.map(message =>
        message.id === messageId ? { ...message, ...outcome } : message
      )
    ])
  )

  if (
    pending &&
    !messagesByUserId[pending.userId]?.some(
      message => message.id === messageId
    )
  ) {
    messagesByUserId[pending.userId] = [
      ...(messagesByUserId[pending.userId] || []),
      {
        id: messageId,
        content: pending.content,
        isSender: true,
        contentType: pending.contentType,
        timestamp: pending.clientCreated,
        ...outcome
      }
    ]
  }

  return {
    pendingMessagesById: Object.fromEntries(
      Object.entries(state.pendingMessagesById).filter(
        ([key]) => key !== messageId
      )
    ),
    messagesByUserId
  }
}

const buildWsMessagePayload = (pending: PendingChatMessage) =>
  converMessagePayloadToFlipper({
    message_id: pending.id,
    room_id: pending.roomId,
    user_id: String(pending.ownerId),
    type: 1,
    text: pending.content,
    client_created: pending.clientCreated
  })

const buildWsTypingPayload = (
  userId: number,
  roomId: number,
  value: 'start' | 'stop'
): Record<string, unknown> => ({
  type: 'TYPING',
  owner_id: userId,
  room: roomId,
  value
})

// ids of the member's latest messages, or null when the history did not load
const fetchSavedMessageIds = async (
  userId: number
): Promise<Set<string> | null> => {
  try {
    assertAccessToken()
    const params = new URLSearchParams()
    params.append('page', '0')
    params.append('page_size', DEFAULT_CHAT_PAGE_SIZE.toString())

    const response = await http.get(
      `/user/${userId}/chat/messages?${params.toString()}`
    )

    const payload = response?.data ?? response
    const results: Record<string, any>[] = payload?.results || []
    return new Set(results.map(raw => normalizeServerMessage(raw).id))
  } catch (error) {
    console.error('Error checking sent chat messages:', error)
    return null
  }
}

const DEFAULT_MEMBER_LIST_PAGE_SIZE = 20

const defaultMemberListFilters = (): ChatListFilters => ({
  search: '',
  category: 0,
  matched_to_me: false,
  last_message_direction: ''
})

const createDefaultMemberList = (): MemberListState => ({
  items: [],
  page: 0,
  hasNextPage: false,
  isLoading: false,
  filters: defaultMemberListFilters(),
  totalEvents: 0,
  roomIdToUserId: {}
})

const formatMemberSubtitle = (gender?: string, age?: number): string | null => {
  const hasGender = Boolean(gender?.trim())
  const hasAge = typeof age === 'number' && !Number.isNaN(age)

  if (hasGender && hasAge) {
    return `${gender!.trim()} - ${age}`
  }
  if (hasGender) {
    return gender!.trim()
  }
  if (hasAge) {
    return String(age)
  }
  return null
}

const isFileMessageType = (type?: string | number): boolean => {
  if (type === undefined || type === null) {
    return false
  }
  const normalized = String(type).trim().toUpperCase()
  return normalized === 'FILE' || normalized === '3'
}

const formatFilePreviewText = (
  owner: number,
  memberUserId: number,
  ownerDisplayName: string,
  professionalUserId?: number | null
): string => {
  if (owner === memberUserId) {
    return 'The member sent a file'
  }
  if (
    professionalUserId !== null &&
    professionalUserId !== undefined &&
    owner === professionalUserId
  ) {
    return 'You sent a file'
  }
  return `${ownerDisplayName || 'Someone'} sent you a file`
}

const getMessageOwnerDisplayName = (
  message: Pick<ChatListMessage, 'owner_name' | 'owner_preferred_name'>
): string =>
  (message.owner_preferred_name || message.owner_name || 'Unknown').trim()

const getChatListMessageTimestamp = (
  message: Pick<ChatListMessage, '_created' | 'created' | 'timestamp'>
): string =>
  toUtcIsoString(message._created ?? message.created ?? message.timestamp) ?? ''

const formatChatListPreview = (
  message: ChatListMessage | null | undefined,
  memberUserId: number,
  professionalUserId?: number | null
) => {
  if (!message) {
    return {
      previewText: '',
      previewCreated: '',
      previewOwnerName: '',
      isFilePreview: false
    }
  }

  const ownerDisplayName = getMessageOwnerDisplayName(message)
  const isFile = isFileMessageType(message.type)
  const previewText = isFile
    ? formatFilePreviewText(
        message.owner,
        memberUserId,
        ownerDisplayName,
        professionalUserId
      )
    : message.value

  return {
    previewText,
    previewCreated: getChatListMessageTimestamp(message),
    previewOwnerName: ownerDisplayName,
    isFilePreview: isFile
  }
}

const isValidChatListResultItem = (raw: unknown): raw is ChatListResultItem => {
  if (!raw || typeof raw !== 'object') {
    return false
  }

  const item = raw as ChatListResultItem
  return Number.isFinite(item.user_id) && Number.isFinite(item.room_id)
}

const filterValidChatListResults = (
  results: unknown[],
  search?: string
): ChatListResultItem[] => {
  const valid: ChatListResultItem[] = []

  results.forEach((item, index) => {
    if (isValidChatListResultItem(item)) {
      valid.push(item)
      return
    }

    console.warn('[Chat] Skipping invalid chatlist result', {
      index,
      search: search?.trim() || undefined,
      item
    })
  })

  return valid
}

const normalizeChatListItem = (
  raw: ChatListResultItem,
  professionalUserId?: number | null
): ChatListItem => {
  const name = `${raw.first_name ?? ''} ${raw.last_name ?? ''}`.trim()
  const preferredName = raw.preferred_name?.trim() || undefined
  const subtitle = formatMemberSubtitle(raw.gender, raw.age)
  const preview = formatChatListPreview(
    raw.message,
    raw.user_id,
    professionalUserId
  )

  return {
    userId: raw.user_id,
    roomId: raw.room_id,
    name: name || `Member ${raw.user_id}`,
    preferredName,
    subtitle,
    previewText: preview.previewText,
    previewCreated: preview.previewCreated,
    previewOwnerName: preview.previewOwnerName,
    isFilePreview: preview.isFilePreview,
    unreadCount: raw.unread_count,
    priority: raw.priority,
    online: Boolean(raw.online)
  }
}

const buildRoomIdToUserIdMap = (
  items: ChatListItem[]
): Record<number, number> => {
  return items.reduce<Record<number, number>>((acc, item) => {
    acc[item.roomId] = item.userId
    return acc
  }, {})
}

const isInboundMessage = (
  ownerId: number,
  memberUserId: number,
  professionalUserId?: number | null
): boolean => {
  if (ownerId === memberUserId) {
    return true
  }
  if (
    professionalUserId !== null &&
    professionalUserId !== undefined &&
    ownerId === professionalUserId
  ) {
    return false
  }
  return ownerId !== professionalUserId
}

const wsEventToChatListMessage = (
  event: FlipperWsEventBase
): ChatListMessage => {
  const owner = event.owner_id ?? 0
  const contentType = event.content_type
  const isFile =
    contentType === 'FILE' ||
    contentType === 3 ||
    String(contentType).toUpperCase() === 'FILE'

  return {
    value: String(event.value ?? ''),
    _created:
      getChatListMessageTimestamp({
        _created: event.timestamp,
        created: event.timestamp,
        timestamp: event.timestamp
      }) || new Date().toISOString(),
    owner,
    owner_name: '',
    type: isFile ? 'FILE' : 'TEXT'
  }
}

const buildMemberListQuery = (
  page: number,
  filters: ChatListFilters,
  pageSize = DEFAULT_MEMBER_LIST_PAGE_SIZE
) => {
  const params = new URLSearchParams()
  params.append('page', page.toString())
  params.append('page_size', pageSize.toString())

  if (filters.search.trim()) {
    params.append('search', filters.search.trim())
  }

  params.append('category', filters.category.toString())

  if (filters.matched_to_me) {
    params.append('matched_to_me', 'true')
  }

  const direction = filters.last_message_direction
  if (direction && direction !== 'all') {
    params.append('last_message_direction', direction)
  }

  return params.toString()
}

export const useChatStore = create<ChatState>()(
  devtools(
    persist(
      (set, get) => ({
        isFlipperWSConnected: false,
        pendingMessagesById: {},
        messagesByUserId: {},
        hasNextPageByUserId: {},
        pageByUserId: {},
        isLoadingMessagesByUserId: {},
        isOtherTypingByUserId: {},
        translationByUserId: {},
        activeUserId: null,
        activeProfessionalUserId: null,
        activeRoomId: null,
        memberList: createDefaultMemberList(),
        memberRoomIds: {},
        incomingMessageNotifications: [],

        setFlipperWSConnected: (isConnected: boolean) => {
          set({ isFlipperWSConnected: isConnected })
        },

        shouldConnectFlipperWS: () => {
          try {
            const accessToken = useAuthStore.getState().accessToken
            return !!accessToken
          } catch (e) {
            return false
          }
        },

        setActiveUserId: (
          userId: number | null,
          professionalUserId?: number | null,
          roomId?: number | null
        ) => {
          set(state => ({
            activeUserId: userId,
            activeProfessionalUserId:
              professionalUserId === undefined ? null : professionalUserId,
            activeRoomId: roomId === undefined ? null : roomId,
            isOtherTypingByUserId:
              userId === null
                ? state.isOtherTypingByUserId
                : { ...state.isOtherTypingByUserId, [userId]: false }
          }))
        },

        addPendingMessage: (message: PendingChatMessage) => {
          set(state => ({
            pendingMessagesById: {
              ...state.pendingMessagesById,
              [message.id]: message
            }
          }))
        },

        removePendingMessageById: (messageId: string) => {
          set(state =>
            settlePendingMessage(state, messageId, {
              isPending: false,
              sendFailed: false,
              errorMessage: undefined
            })
          )
        },

        failPendingMessageById: (messageId: string, errorMessage: string) => {
          set(state =>
            settlePendingMessage(state, messageId, {
              isPending: false,
              sendFailed: true,
              errorMessage
            })
          )
        },

        // Flipper confirms a message once, on the sockets open at that moment.
        // When a message it already saved comes in again, it stops reading
        // that socket and leaves it open. A pending message whose confirmation
        // was lost is in the history, and sending it again would silence each
        // new socket, holding back every message queued behind it. So only
        // the ones missing there go again.
        retrySendPendingMessages: async () => {
          if (!isSocketOpen()) {
            return
          }

          const openSocket = socket
          const unsavedMessages = await confirmSavedPendingMessages(get)

          // a newer socket sends them when it opens
          if (socket !== openSocket || !isSocketOpen()) {
            return
          }

          unsavedMessages.forEach(message => {
            if (
              !message.roomId ||
              !message.ownerId ||
              (message.attempts ?? 0) >= MAX_SEND_ATTEMPTS
            ) {
              get().failPendingMessageById(message.id, 'Failed to send message')
              return
            }

            sendPendingMessage(get, message)
          })

          // the ones whose history did not load are checked again
          if (getPendingTextMessages(get()).length > 0) {
            scheduleDeliveryCheck(get)
          }
        },

        connectSocket: () => {
          if (!get().shouldConnectFlipperWS()) {
            reconnectTimeout = setTimeout(() => {
              get().connectSocket()
            }, 1000)
            return
          }

          intentionalDisconnect = false

          clearHeartbeat()

          if (socket) {
            if (socket.readyState === WebSocket.OPEN) {
              set({ isFlipperWSConnected: true })
              return
            }
            if (socket.readyState === WebSocket.CONNECTING) {
              return
            }

            socket.onopen = null
            socket.onmessage = null
            socket.onerror = null
            socket.onclose = null
            socket.close()
            socket = null
          }

          const accessToken = useAuthStore.getState().accessToken

          socket = websocketClient({
            queries: { token: accessToken },
            handleOpen: () => {
              get().setFlipperWSConnected(true)
              startHeartbeat(get)
              get().retrySendPendingMessages()

              const activeUserId = get().activeUserId
              if (activeUserId) {
                get().fetchMessages(
                  activeUserId,
                  0,
                  DEFAULT_CHAT_PAGE_SIZE,
                  undefined,
                  get().activeProfessionalUserId ?? undefined
                )
              }
            },
            handleReceive: (event: MessageEvent) => {
              try {
                const data = parseFlipperWsEvent(event.data)
                if (!data) {
                  return
                }

                const eventType = normalizeWsEventType(data.type)

                if (eventType === 'MSG_RECEIVED') {
                  get().removePendingMessageById(String(data.value))
                  return
                }

                if (eventType === 'SYSTEM') {
                  const messageId = String(data.id ?? '')
                  if (messageId && get().pendingMessagesById[messageId]) {
                    get().failPendingMessageById(
                      messageId,
                      String(data.value ?? 'Failed to send message')
                    )
                  }
                  return
                }

                if (eventType === 'MESSAGE') {
                  get().upsertMemberListFromWs(data)

                  if (!belongsToActiveRoom(data, get().activeRoomId)) {
                    return
                  }

                  const userId = get().activeUserId
                  if (!userId) {
                    return
                  }

                  const pending = get().pendingMessagesById[data.id]
                  const normalized = normalizeServerMessage(data)
                  const professionalUserId =
                    pending?.ownerId ??
                    get().activeProfessionalUserId ??
                    undefined
                  const [message] = processServerMessages(
                    [normalized],
                    userId,
                    professionalUserId
                  )

                  if (!message) {
                    return
                  }

                  const documentId = message.documentId

                  set(state => {
                    const existing = state.messagesByUserId[userId] || []
                    const withoutDuplicateFile =
                      message.contentType === 'FILE' && documentId !== undefined
                        ? existing.filter(
                            item =>
                              !(
                                item.contentType === 'FILE' &&
                                item.documentId === documentId &&
                                item.id !== message.id
                              )
                          )
                        : existing

                    return {
                      messagesByUserId: appendMessageForUser(
                        {
                          ...state.messagesByUserId,
                          [userId]: withoutDuplicateFile
                        },
                        userId,
                        message
                      )
                    }
                  })

                  if (pending) {
                    get().removePendingMessageById(data.id)
                  }
                  return
                }

                if (isRoomScopedEvent(eventType)) {
                  if (!belongsToActiveRoom(data, get().activeRoomId)) {
                    return
                  }
                }

                if (eventType === 'TYPING') {
                  const userId = get().activeUserId
                  if (!userId) {
                    return
                  }

                  set(state => ({
                    isOtherTypingByUserId: {
                      ...state.isOtherTypingByUserId,
                      [userId]: data.value === 'start'
                    }
                  }))
                }
              } catch (error) {
                console.error('Error handling websocket message:', error)
              }
            },
            handleError: () => {
              clearHeartbeat()
              get().setFlipperWSConnected(false)
              scheduleReconnect(get)
            },
            handleClose: () => {
              clearHeartbeat()
              get().setFlipperWSConnected(false)
              if (!intentionalDisconnect) {
                scheduleReconnect(get)
              }
            }
          })
        },

        disconnectSocket: () => {
          intentionalDisconnect = true

          clearHeartbeat()
          clearDeliveryCheck()

          if (reconnectTimeout) {
            clearTimeout(reconnectTimeout)
            reconnectTimeout = null
          }

          if (socket) {
            socket.onopen = null
            socket.onmessage = null
            socket.onerror = null
            socket.onclose = null
            socket.close()
            socket = null
          }

          set({ isFlipperWSConnected: false })
        },

        fetchMessages: async (
          userId: number,
          page = 0,
          pageSize = DEFAULT_CHAT_PAGE_SIZE,
          language?: string,
          professionalUserId?: number
        ) => {
          set(state => ({
            isLoadingMessagesByUserId: {
              ...state.isLoadingMessagesByUserId,
              [userId]: true
            }
          }))

          try {
            assertAccessToken()
            const params = new URLSearchParams()
            params.append('page', page.toString())
            params.append('page_size', pageSize.toString())
            if (language) {
              params.append('language', language)
            }

            const response = await http.get(
              `/user/${userId}/chat/messages?${params.toString()}`
            )

            const payload = response?.data ?? response
            const results: ServerMessage[] = (payload?.results || []).map(
              normalizeServerMessage
            )
            const hasNextPage = !!payload?.has_next_page
            const resolvedProfessionalUserId =
              professionalUserId ?? get().activeProfessionalUserId ?? undefined
            const mapped = processServerMessages(
              results,
              userId,
              resolvedProfessionalUserId
            )

            set(state => {
              const current = state.messagesByUserId[userId] || []
              // page 0 starts the conversation over, but a message that failed
              // to send, or is still on its way, exists only here
              const existing =
                page === 0
                  ? current.filter(
                      message => message.sendFailed || message.isPending
                    )
                  : current
              return {
                messagesByUserId: {
                  ...state.messagesByUserId,
                  [userId]: mergeMessages(existing, mapped)
                },
                hasNextPageByUserId: {
                  ...state.hasNextPageByUserId,
                  [userId]: hasNextPage
                },
                pageByUserId: {
                  ...state.pageByUserId,
                  [userId]: page
                },
                isLoadingMessagesByUserId: {
                  ...state.isLoadingMessagesByUserId,
                  [userId]: false
                }
              }
            })
          } catch (error) {
            console.error('Error fetching chat messages:', error)
            set(state => ({
              isLoadingMessagesByUserId: {
                ...state.isLoadingMessagesByUserId,
                [userId]: false
              }
            }))
          }
        },

        markAsRead: async (userId: number) => {
          try {
            assertAccessToken()
            await http.post(`/user/${userId}/chat/read`)

            set(state => ({
              memberList: {
                ...state.memberList,
                items: state.memberList.items.map(item =>
                  item.userId === userId ? { ...item, unreadCount: 0 } : item
                )
              }
            }))

            return true
          } catch (error) {
            console.error('Error marking chat as read:', error)
            return false
          }
        },

        setTranslation: async (
          userId: number,
          on: boolean,
          language: string
        ) => {
          try {
            assertAccessToken()
            await http.post(`/user/${userId}/translate`, { on, language })

            set(state => ({
              translationByUserId: {
                ...state.translationByUserId,
                [userId]: { on, language }
              }
            }))

            return true
          } catch (error) {
            console.error('Error setting translation:', error)
            return false
          }
        },

        saveLanguageSetting: async (userId: number, language: string) => {
          try {
            assertAccessToken()
            await http.post(`/user/${userId}/settings`, { language })
            return true
          } catch (error) {
            console.error('Error saving language setting:', error)
            return false
          }
        },

        uploadDocument: async (userId: number, file: File) => {
          try {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('type', 'CHAT')
            assertAccessToken()
            const data = await http.post(
              `/user/${userId}/document/upload`,
              formData
            )

            const documentId = data?.entity_id ?? data?.id
            if (!documentId) {
              return null
            }

            return {
              documentId,
              documents: {
                file_name: data.file_name ?? file.name,
                file_size: data.filesize ?? file.size,
                mime_type: data.mimetype ?? file.type
              }
            }
          } catch (error) {
            console.error('Error uploading document:', error)
            return null
          }
        },

        getDocuments: async (userId: number) => {
          try {
            assertAccessToken()
            const response = await http.get(`/user/${userId}/documents`)

            return response || null
          } catch (error) {
            console.error('Error fetching documents:', error)
            return null
          }
        },

        getDocumentUrl: async (userId: number, documentId: number) => {
          try {
            assertAccessToken()
            const response = await http.get(
              `/user/${userId}/document/${documentId}`
            )

            return response?.download_url || response?.url || null
          } catch (error) {
            console.error('Error fetching document url:', error)
            return null
          }
        },

        sendTextMessage: (
          userId: number,
          text: string,
          context: { roomId: number; ownerId: number }
        ) => {
          const trimmed = text.trim()
          if (!trimmed) {
            return
          }

          const id = uuidHex()
          const clientCreated = new Date().toISOString()
          const pending: PendingChatMessage = {
            id,
            userId,
            roomId: context.roomId,
            ownerId: context.ownerId,
            content: trimmed,
            contentType: 'TEXT',
            value: trimmed,
            clientCreated
          }

          const optimistic: ChatMessage = {
            id,
            content: trimmed,
            isSender: true,
            contentType: 'TEXT',
            timestamp: clientCreated,
            isPending: true
          }

          get().addPendingMessage(pending)
          set(state => ({
            messagesByUserId: appendMessageForUser(
              state.messagesByUserId,
              userId,
              optimistic
            )
          }))

          if (!context.roomId || !context.ownerId) {
            get().failPendingMessageById(
              id,
              'Unable to send message. Chat room is not ready yet.'
            )
            return id
          }

          if (isSocketOpen()) {
            try {
              sendPendingMessage(get, pending)
            } catch (error) {
              console.error('Failed to send text message:', error)
              get().connectSocket()
              get().failPendingMessageById(id, 'Failed to send message')
            }
          } else {
            get().connectSocket()
          }

          return id
        },

        sendFileMessage: (
          userId: number,
          documentId: number,
          _context: { roomId: number; ownerId: number },
          documents?: ChatDocument
        ) => {
          const id = uuidHex()
          const clientCreated = new Date().toISOString()
          const message: ChatMessage = {
            id,
            content: documents?.file_name || 'File',
            isSender: true,
            contentType: 'FILE',
            documents,
            documentId,
            timestamp: clientCreated
          }

          set(state => {
            const existing = state.messagesByUserId[userId] || []
            const withoutDuplicateFile = existing.filter(
              item =>
                !(item.contentType === 'FILE' && item.documentId === documentId)
            )

            return {
              messagesByUserId: appendMessageForUser(
                { ...state.messagesByUserId, [userId]: withoutDuplicateFile },
                userId,
                message
              )
            }
          })
        },

        sendTyping: (
          userId: number,
          roomId: number,
          value: 'start' | 'stop'
        ) => {
          if (!isSocketOpen()) {
            return
          }

          try {
            socket!.send(
              JSON.stringify(buildWsTypingPayload(userId, roomId, value))
            )
          } catch (error) {
            console.error('Failed to send typing event:', error)
          }
        },

        getChatSuggestionsDashboard: async (
          page: number,
          page_size: number
        ) => {
          try {
            assertAccessToken()
            const params = new URLSearchParams()
            params.append('page', page.toString())
            params.append('page_size', page_size.toString())
            const endpoint = `/pharmacist/chat-suggestions?${params.toString()}`
            const response = await http.get(endpoint)

            return response
          } catch (error) {
            console.error('Error fetching chat suggestions dashboard:', error)
            return null
          }
        },

        getChatSuggestionsByUserId: async (userId: number) => {
          try {
            assertAccessToken()
            const endpoint = `/pharmacist/chat-suggestions/${userId}`
            const response = await http.get(endpoint)

            if (!response?.data) {
              return null
            }
            return response.data
          } catch (error) {
            console.error('Error fetching chat suggestions by user ID:', error)
            return null
          }
        },

        sendChatSuggestion: async (
          userId: number,
          suggestionId: number,
          revision?: string
        ) => {
          try {
            assertAccessToken()
            const endpoint = `/pharmacist/chat-suggestions/${userId}/send`
            const payload: { suggestion_id: number; revision?: string } = {
              suggestion_id: suggestionId
            }
            if (revision) {
              payload.revision = revision
            }
            const response = await http.post(endpoint, payload)

            return response?.success
          } catch (error) {
            console.error('Error sending chat suggestion:', error)
            return false
          }
        },

        rejectChatSuggestion: async (userId: number, suggestionId: number) => {
          try {
            assertAccessToken()
            const endpoint = `/pharmacist/chat-suggestions/${userId}/reject`
            const response = await http.post(endpoint, {
              suggestion_id: suggestionId
            })

            return response?.success
          } catch (error) {
            console.error('Error rejecting chat suggestion:', error)
            return false
          }
        },

        fetchMemberList: async ({ reset = false, professionalUserId } = {}) => {
          if (!useAuthStore.getState().accessToken) {
            return
          }

          const { memberList } = get()
          if (memberList.isLoading && !reset) {
            return
          }

          const page = reset ? 0 : memberList.page
          const profId =
            professionalUserId ?? get().activeProfessionalUserId ?? undefined

          set({
            memberList: { ...get().memberList, isLoading: true }
          })

          try {
            assertAccessToken()
            const query = buildMemberListQuery(page, memberList.filters)
            const response = await http.get(`/chatlist?${query}`)

            const payload: ChatListResponse = response?.data ?? response
            const results = payload?.results ?? []
            const validResults = filterValidChatListResults(
              results,
              memberList.filters.search
            )
            const normalized = validResults.map(item =>
              normalizeChatListItem(item, profId)
            )
            const mergedItems = reset
              ? normalized
              : [...memberList.items, ...normalized]

            set({
              memberList: {
                ...get().memberList,
                items: mergedItems,
                page,
                hasNextPage: !!payload?.has_next_page,
                totalEvents: payload?.total_events ?? 0,
                roomIdToUserId: buildRoomIdToUserIdMap(mergedItems)
              }
            })
          } catch (error) {
            console.error('Error fetching member list:', error)
          } finally {
            set({
              memberList: { ...get().memberList, isLoading: false }
            })
          }
        },

        loadMoreMemberList: async ({ professionalUserId } = {}) => {
          const { memberList } = get()
          if (!memberList.hasNextPage || memberList.isLoading) {
            return
          }

          set({
            memberList: { ...memberList, page: memberList.page + 1 }
          })

          await get().fetchMemberList({
            reset: false,
            professionalUserId
          })
        },

        setMemberListFilters: async (partial, options = {}) => {
          set(state => ({
            memberList: {
              ...state.memberList,
              filters: { ...state.memberList.filters, ...partial },
              page: 0
            }
          }))

          await get().fetchMemberList({
            reset: true,
            professionalUserId: options.professionalUserId
          })
        },

        upsertMemberListFromWs: (event: FlipperWsEventBase) => {
          const roomId = typeof event.room === 'number' ? event.room : undefined
          if (!roomId) {
            return
          }

          const state = get()
          const { memberList } = state
          const professionalUserId = state.activeProfessionalUserId
          let memberUserId =
            memberList.roomIdToUserId[roomId] ??
            (typeof event.user_id === 'number' ? event.user_id : undefined) ??
            (typeof event.target_user_id === 'number'
              ? event.target_user_id
              : undefined)

          if (!memberUserId) {
            const match = memberList.items.find(item => item.roomId === roomId)
            memberUserId = match?.userId
          }

          if (!memberUserId) {
            return
          }

          const ownerId = event.owner_id ?? 0
          const listMessage = wsEventToChatListMessage(event)
          const existingItem = memberList.items.find(
            item => item.userId === memberUserId
          )

          if (existingItem && !listMessage.owner_name) {
            listMessage.owner_name = existingItem.previewOwnerName
          }

          const preview = formatChatListPreview(
            listMessage,
            memberUserId,
            professionalUserId
          )

          const updatedItem: ChatListItem = existingItem
            ? {
                ...existingItem,
                previewText: preview.previewText,
                previewCreated: preview.previewCreated,
                previewOwnerName: preview.previewOwnerName,
                isFilePreview: preview.isFilePreview
              }
            : {
                userId: memberUserId,
                roomId,
                name: `Member ${memberUserId}`,
                subtitle: null,
                previewText: preview.previewText,
                previewCreated: preview.previewCreated,
                previewOwnerName: preview.previewOwnerName,
                isFilePreview: preview.isFilePreview,
                online: false
              }

          const remaining = memberList.items.filter(
            item => item.userId !== memberUserId
          )
          const items = [updatedItem, ...remaining]
          const roomIdToUserId = {
            ...memberList.roomIdToUserId,
            [roomId]: memberUserId
          }

          const isInbound = isInboundMessage(
            ownerId,
            memberUserId,
            professionalUserId
          )

          set({
            memberList: {
              ...memberList,
              items,
              roomIdToUserId
            }
          })

          if (
            isInbound &&
            memberUserId !== state.activeUserId &&
            preview.previewText
          ) {
            get().pushIncomingMessageNotification({
              userId: memberUserId,
              name: (
                updatedItem.preferredName ||
                updatedItem.name ||
                ''
              ).trim(),
              preview: preview.previewText,
              roomId,
              isFilePreview: preview.isFilePreview
            })
          }
        },

        pushIncomingMessageNotification: (
          notification: Omit<IncomingMessageNotification, 'id'>
        ) => {
          set(state => ({
            incomingMessageNotifications: [
              ...state.incomingMessageNotifications,
              { ...notification, id: uuidHex() }
            ]
          }))
        },

        removeIncomingMessageNotification: (id: string) => {
          set(state => ({
            incomingMessageNotifications:
              state.incomingMessageNotifications.filter(item => item.id !== id)
          }))
        },

        clearIncomingMessageNotifications: () => {
          set({ incomingMessageNotifications: [] })
        },

        getMemberListRoomId: (userId: number) => {
          return (
            get().memberList.items.find(item => item.userId === userId)
              ?.roomId ?? get().memberRoomIds[userId]
          )
        },

        // a chat opened by URL (reports, dashboard, members) is often for a
        // member outside the loaded pages of the list, which left the room
        // unknown: messages failed with "Chat room is not ready yet" and live
        // events for the room were dropped. The list search matches user ids.
        fetchMemberRoomId: async (userId: number) => {
          const knownRoomId = get().getMemberListRoomId(userId)
          if (
            knownRoomId !== undefined ||
            !useAuthStore.getState().accessToken
          ) {
            return knownRoomId
          }

          try {
            const query = buildMemberListQuery(0, {
              ...defaultMemberListFilters(),
              search: String(userId)
            })
            const response = await http.get(`/chatlist?${query}`)
            const payload: ChatListResponse = response?.data ?? response
            const roomId = filterValidChatListResults(
              payload?.results ?? []
            ).find(item => item.user_id === userId)?.room_id

            if (roomId !== undefined) {
              set(state => ({
                memberRoomIds: { ...state.memberRoomIds, [userId]: roomId }
              }))
            }

            return roomId
          } catch (error) {
            console.error('Error fetching member room:', error)
            return undefined
          }
        },

        clear: () => {
          get().disconnectSocket()
          set({
            pendingMessagesById: {},
            messagesByUserId: {},
            hasNextPageByUserId: {},
            pageByUserId: {},
            isLoadingMessagesByUserId: {},
            isOtherTypingByUserId: {},
            translationByUserId: {},
            activeUserId: null,
            activeProfessionalUserId: null,
            activeRoomId: null,
            memberList: createDefaultMemberList(),
            memberRoomIds: {},
            incomingMessageNotifications: []
          })
        }
      }),
      {
        name: 'chat-zustand-storage',
        storage: createJSONStorage(() => sessionStorage),
        partialize: state => ({
          pendingMessagesById: state.pendingMessagesById
        })
      }
    )
  )
)

function clearHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
  }
}

function startHeartbeat(getState: () => ChatState) {
  clearHeartbeat()

  heartbeatInterval = setInterval(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      return
    }

    console.warn('Flipper heartbeat failed')
    getState().setFlipperWSConnected(false)
    scheduleReconnect(getState)
  }, HEARTBEAT_INTERVAL)
}

function scheduleReconnect(getState: () => ChatState) {
  if (reconnectTimeout || intentionalDisconnect) {
    return
  }

  reconnectTimeout = setTimeout(() => {
    reconnectTimeout = null
    getState().connectSocket()
  }, RECONNECT_INTERVAL)
}

function sendPendingMessage(
  getState: () => ChatState,
  message: PendingChatMessage
) {
  socket!.send(JSON.stringify(buildWsMessagePayload(message)))
  getState().addPendingMessage({
    ...message,
    attempts: (message.attempts ?? 0) + 1,
    sentAt: Date.now()
  })
  scheduleDeliveryCheck(getState)
}

function getPendingTextMessages(state: ChatState) {
  return Object.values(state.pendingMessagesById).filter(
    message => message.contentType !== 'FILE'
  )
}

function clearDeliveryCheck() {
  if (deliveryCheckTimeout) {
    clearTimeout(deliveryCheckTimeout)
    deliveryCheckTimeout = null
  }
}

// Runs when the oldest send still waiting reaches the delay, so messages sent
// one after another on a silent socket do not keep pushing the check back. A
// message already past it (its history did not load) waits a whole delay.
function scheduleDeliveryCheck(getState: () => ChatState) {
  if (deliveryCheckTimeout) {
    return
  }

  const now = Date.now()
  const waits = getPendingTextMessages(getState())
    .map(message => (message.sentAt ?? now) + DELIVERY_CHECK_DELAY - now)
    .filter(wait => wait > 0)

  deliveryCheckTimeout = setTimeout(
    () => {
      deliveryCheckTimeout = null
      checkPendingDelivery(getState)
    },
    waits.length > 0 ? Math.min(...waits) : DELIVERY_CHECK_DELAY
  )
}

// A socket can look open and deliver nothing: Flipper stops reading it after
// any error while handling a message and leaves it open, and a connection that
// died without a close (the computer slept, the network changed) stays OPEN
// in the browser. A message unconfirmed after the delay and missing from the
// history means the socket stopped delivering: it is replaced, and the new one
// sends the message again when it opens.
async function checkPendingDelivery(getState: () => ChatState) {
  const checkedSocket = socket
  const unsavedMessages = await confirmSavedPendingMessages(getState)

  // a closed or newer socket sends them when it opens
  if (socket !== checkedSocket || !isSocketOpen()) {
    return
  }

  // a message sent moments ago may still be on its way
  const overdueMessages = unsavedMessages.filter(
    message => Date.now() - (message.sentAt ?? 0) >= DELIVERY_CHECK_DELAY
  )

  if (overdueMessages.length > 0) {
    console.warn('Flipper did not confirm a chat message, reconnecting')
    getState().disconnectSocket()
    getState().connectSocket()
    return
  }

  // the ones still within the delay, or whose history did not load, wait
  if (getPendingTextMessages(getState()).length > 0) {
    scheduleDeliveryCheck(getState)
  }
}

// Confirms the pending messages the member's history already holds and returns
// the ones it does not, in the order they were written. Messages of a member
// whose history did not load stay pending and out of the result.
async function confirmSavedPendingMessages(
  getState: () => ChatState
): Promise<PendingChatMessage[]> {
  const pendingMessages = getPendingTextMessages(getState())
  const userIds = Array.from(new Set(pendingMessages.map(m => m.userId)))
  const savedIdsByUserId = new Map(
    await Promise.all(
      userIds.map(
        async userId => [userId, await fetchSavedMessageIds(userId)] as const
      )
    )
  )

  const unsavedMessages: PendingChatMessage[] = []
  pendingMessages.forEach(message => {
    const savedIds = savedIdsByUserId.get(message.userId)
    // confirmed by the socket meanwhile, or the history did not load
    if (!savedIds || !getState().pendingMessagesById[message.id]) {
      return
    }

    if (savedIds.has(message.id)) {
      getState().removePendingMessageById(message.id)
    } else {
      unsavedMessages.push(message)
    }
  })

  return unsavedMessages
}
